import { useMutation } from "@tanstack/react-query";
import { useDraftMode } from "../contexts/DraftModeContext";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { backendService } from "../services/backend.service";
import { trackEvent } from "../utils/analytics";
import type { CensusGrouping, ChangesSource } from "../utils/models";

export const useHandleCensus = () => {
	const { refreshDepartmentData, chosenDepartment, showErrorSnackbar, setHospitalData } = useHospitalData();
	const { isDraftMode } = useDraftMode();

	const {
		mutateAsync: updateCensusOverride,
		isPending: isUpdatingCensus,
		error: updateCensusError,
	} = useMutation({
		mutationFn: async ({
			date,
			shiftType,
			overrideValue,
			note,
			source,
			weekIndex,
			shiftIndex,
			dayIndex,
			locationCensus,
		}: {
			date: string;
			shiftType: string;
			overrideValue: number;
			note?: string;
			source: ChangesSource;
			weekIndex: number;
			shiftIndex: number;
			dayIndex: number;
			locationCensus?: CensusGrouping;
		}) => {
			// Update local state optimistically
			setHospitalData((prev) => {
				const newData = { ...prev };
				const week = newData.weeks[weekIndex];
				if (week) {
					const shift = week.weeklyShifts[shiftIndex];
					if (shift) {
						const day = shift.days[dayIndex];
						if (day) {
							day.census_override = {
								override_value: overrideValue,
								note: note || "",
								user_id: day.census_override?.user_id || "optimistic",
								updated_at: new Date().toISOString(),
							};
							// Retyping answers the "EMR replaced your number" notice.
							day.census_replacement = undefined;
							if (locationCensus) {
								day.census_grouping = locationCensus;
							}
						}
					}
				}
				return newData;
			});

			// If draft mode is enabled, skip backend call (auto-tracking will detect the change)
			if (isDraftMode) {
				return { success: true };
			}

			await backendService.createOrUpdateCensusOverride({
				department_id: chosenDepartment.department_id,
				date,
				shift_type: shiftType,
				override_value: overrideValue,
				note,
				source,
				location_census: locationCensus,
			});

			trackEvent("Edit census menu", {
				action: "update census",
				date,
				shift_type: shiftType,
				census: overrideValue,
			});

			if (!isDraftMode) {
				await refreshDepartmentData(chosenDepartment.department_id, false);
			}

			return { success: true };
		},
		onError: (error, variables) => {
			trackEvent("Edit census menu", {
				action: "update census failed",
				date: variables.date,
				shift_type: variables.shiftType,
				census: variables.overrideValue,
			});
			showErrorSnackbar("Failed to update census", { date: variables.date, shift_type: variables.shiftType }, error);
		},
	});

	const {
		mutateAsync: deleteCensusOverride,
		isPending: isDeletingCensus,
		error: deleteCensusError,
	} = useMutation({
		mutationFn: async ({
			date,
			shiftType,
			source,
			weekIndex,
			shiftIndex,
			dayIndex,
		}: {
			date: string;
			shiftType: string;
			source: ChangesSource;
			weekIndex: number;
			shiftIndex: number;
			dayIndex: number;
		}) => {
			// Update local state optimistically
			setHospitalData((prev) => {
				const newData = { ...prev };
				const week = newData.weeks[weekIndex];
				if (week) {
					const shift = week.weeklyShifts[shiftIndex];
					if (shift) {
						const day = shift.days[dayIndex];
						if (day) {
							day.census_override = undefined;
						}
					}
				}
				return newData;
			});

			// If draft mode is enabled, skip backend call (auto-tracking will detect the change)
			if (isDraftMode) {
				return { success: true };
			}

			// Not in draft mode, call the API
			await backendService.deleteCensusOverride({
				department_id: chosenDepartment.department_id,
				date,
				shift_type: shiftType,
				source,
			});

			trackEvent("Edit census menu", {
				action: "delete census override",
				date,
				shift_type: shiftType,
			});

			if (!isDraftMode) {
				await refreshDepartmentData(chosenDepartment.department_id, false);
			}

			return { success: true };
		},
		onError: (error, variables) => {
			trackEvent("Edit census menu", {
				action: "delete census override failed",
				date: variables.date,
				shift_type: variables.shiftType,
			});
			showErrorSnackbar("Failed to delete census override", { date: variables.date, shift_type: variables.shiftType }, error);
		},
	});

	const { mutateAsync: acceptCensusReplacement, isPending: isAcceptingCensus } = useMutation({
		mutationFn: async ({ date, weekIndex, dayIndex }: { date: string; weekIndex: number; dayIndex: number }) => {
			setHospitalData((prev) => {
				const newData = { ...prev, weeks: [...prev.weeks] };
				const week = newData.weeks[weekIndex];
				if (week) {
					newData.weeks[weekIndex] = {
						...week,
						weeklyShifts: week.weeklyShifts.map((shift) => ({
							...shift,
							days: shift.days.map((day, index) => (index === dayIndex ? { ...day, census_replacement: undefined } : day)),
						})),
					};
				}
				return newData;
			});

			// Saved even in draft mode: the notice is not schedule data, and the draft's change tracking
			// never sees it, so publishing the draft would not dismiss it.
			await backendService.dismissCensusReplacement({
				department_id: chosenDepartment.department_id,
				date,
			});
			// The notice is already gone locally. Reloading the schedule here rebuilds the weeks array and
			// the weekly grid snaps back to the route date, so Accept jumped to another week instead of
			// closing the menu on the day the admin was looking at.
			return { success: true };
		},
		onError: (error, variables) => {
			trackEvent("Edit census menu", { action: "accept census replacement failed", date: variables.date });
			showErrorSnackbar("Failed to accept the census replacement", { date: variables.date }, error);
		},
	});

	return {
		updateCensusOverride,
		deleteCensusOverride,
		acceptCensusReplacement,
		isLoading: {
			updateCensus: isUpdatingCensus,
			deleteCensus: isDeletingCensus,
			acceptCensus: isAcceptingCensus,
		},
		error: {
			updateCensus: updateCensusError,
			deleteCensus: deleteCensusError,
		},
	};
};
